import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import styles from './SampleAnalysisPage.module.css';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  ExternalOrderListItem,
  fetchExternalOrders,
} from './services/sampleAnalysisAPI';
import { MOCK_ORDERS } from './services/mockData';

const USE_MOCK_DATA = false;

interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

const DEFAULT_PAGINATION: PaginationState = {
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
};
const ALLOWED_OPERACOES_FISCAIS = new Set([21]);

const SampleAnalysisPage: React.FC = () => {
  const [orders, setOrders] = useState<ExternalOrderListItem[]>([]);
  const [loadingList, setLoadingList] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION);
  
  // -- Filtros --
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [operacaoFilter, setOperacaoFilter] = useState('all');
  const [periodo, setPeriodo] = useState('mensal'); // semanal, mensal, custom
  const [dataI, setDataI] = useState('');
  const [dataF, setDataF] = useState('');

  const navigate = useNavigate();

  // Calcula datas automáticas baseadas no período
  useEffect(() => {
    if (periodo === 'custom') return;

    const now = new Date();
    let start = new Date();

    if (periodo === 'semanal') {
      start.setDate(now.getDate() - 7);
    } else if (periodo === 'mensal') {
      start.setDate(now.getDate() - 30);
    }

    const formatDateISO = (d: Date) => d.toISOString().split('T')[0];
    setDataI(formatDateISO(start));
    setDataF(formatDateISO(now));
  }, [periodo]);

  const loadList = async (page: number) => {
    try {
      setLoadingList(true);
      setError(null);

      if (USE_MOCK_DATA) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        setOrders(MOCK_ORDERS);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: MOCK_ORDERS.length,
        });
        return;
      }

      const response = await fetchExternalOrders(page, 20, {
        search,
        status: statusFilter,
        operacao: operacaoFilter,
        dataI,
        dataF
      });
      
      const payload = response.data as any;
      const list = Array.isArray(payload)
        ? payload
        : payload?.items || payload?.orders || [];

      setOrders(list);
      setPagination(
        payload?.pagination ?? {
          currentPage: page,
          totalPages: 1,
          totalItems: list.length,
        }
      );

    } catch (err) {
      console.error(err);
      setError('Não foi possível carregar os pedidos externos.');
    } finally {
      setLoadingList(false);
    }
  };

  // Recarrega quando filtros mudam (exceto search, que usamos debounce ou botão)
  useEffect(() => {
    loadList(1);
    setCurrentPage(1);
  }, [statusFilter, operacaoFilter, dataI, dataF]);

  // Busca inicial e paginação
  useEffect(() => {
    loadList(currentPage);
  }, [currentPage]);

  // Debounce para busca de texto
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.length === 0 || search.length > 2) {
        loadList(1);
        setCurrentPage(1);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [search]);

  const formatCurrency = (value?: number) => {
    if (typeof value !== 'number') return '--';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
  };

  const handleRowClick = (id: number) => {
    navigate(`/amostras-pedidos/${id}`);
  };

  const getPedidoBadge = (order: ExternalOrderListItem) => {
    switch (order.pedido_situacao) {
      case 1:
        return { label: 'Aguardando Amostras', color: '#eff6ff', textColor: '#2563eb' };
      case 3:
        return { label: 'Finalizado', color: '#dcfce3', textColor: '#15803d' };
      case 2:
        return { label: 'Recusado', color: '#fee2e2', textColor: '#b91c1c' };
      default:
        return { label: 'Sem status', color: '#f1f5f9', textColor: '#64748b' };
    }
  };

  const DOT_COLORS: Record<string, string> = {
    respondida: '#16a34a',  // verde
    disponivel: '#ea580c',  // laranja
    aguardando: '#dc2626',  // vermelho
  };

  const ETAPA_LABELS_DOT = ['10d', '30d', '60d', 'Fim'];

  const renderEtapasDots = (order: ExternalOrderListItem) => {
    const raw = order.etapas_status;
    if (!raw) {
      // Sem dados de etapa: 4 bolinhas cinza
      return (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          {ETAPA_LABELS_DOT.map((label, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{
                width: 12, height: 12, borderRadius: '50%',
                backgroundColor: '#d1d5db',
              }} />
              <span style={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600 }}>{label}</span>
            </div>
          ))}
        </div>
      );
    }

    const statuses = raw.split(',');
    return (
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        {statuses.map((s, i) => {
          const color = DOT_COLORS[s.trim()] || '#d1d5db';
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{
                width: 12, height: 12, borderRadius: '50%',
                backgroundColor: color,
                boxShadow: s.trim() === 'respondida' ? `0 0 0 2px ${color}40` : undefined,
              }} />
              <span style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>{ETAPA_LABELS_DOT[i]}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <h1>Pedidos de Amostra</h1>
      </div>

      <section className={styles.registrySection}>
        <div className={styles.sectionHeader}>
          <div className={styles.filters}>
            <div className={styles.leftFilters}>
              {/* Busca por texto */}
              <div className={styles.searchGroup}>
                <input
                  type="text"
                  placeholder="Buscar por ID, Cliente, CNPJ..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              {/* Status */}
              <div className={styles.filterItem}>
                <label>Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="all">Todos</option>
                  <option value="Aprovado">Aprovado</option>
                  <option value="Reprovado">Reprovado</option>
                  <option value="Misto">Misto</option>
                  <option value="Pendente">Pendente</option>
                  <option value="Em Teste">Em Teste</option>
                  <option value="Expirada">Expirada</option>
                </select>
              </div>

              {/* Operação */}
              <div className={styles.filterItem}>
                <label>Operação:</label>
                <select
                  value={operacaoFilter}
                  onChange={(e) => setOperacaoFilter(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="all">Todas</option>
                  <option value="3">3 - Amostra</option>
                  <option value="21">21 - Amostra Homolog.</option>
                </select>
              </div>
            </div>

            <div className={styles.rightFilters}>
              {/* Período (Estilo Dashboard) */}
              <div className={styles.periodButtons}>
                {['semanal', 'mensal', 'custom'].map(p => (
                  <button
                    key={p}
                    onClick={() => setPeriodo(p)}
                    className={`${styles.periodBtn} ${periodo === p ? styles.periodBtnActive : ''}`}
                  >
                    {p === 'custom' ? 'Personalizado' : p === 'semanal' ? '7 dias' : '30 dias'}
                  </button>
                ))}
              </div>

              {/* Data Customizada */}
              {periodo === 'custom' && (
                <div className={styles.dateRangeContainer}>
                  <input
                    type="date"
                    value={dataI}
                    onChange={(e) => setDataI(e.target.value)}
                    className={styles.dateInput}
                  />
                  <span style={{ color: '#94a3b8' }}>até</span>
                  <input
                    type="date"
                    value={dataF}
                    onChange={(e) => setDataF(e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {error && <p className={styles.feedbackError}>{error}</p>}

        <div style={{ overflowX: 'auto' }}>
          <table className={styles.tableContainer}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Cliente</th>
                <th>Aprovação</th>
                <th>Etapas</th>
                <th>Operação</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loadingList ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td><Skeleton width={40} /></td>
                    <td><Skeleton width={200} /></td>
                    <td><Skeleton width={120} /></td>
                    <td><Skeleton width={120} /></td>
                    <td><Skeleton width={100} /></td>
                    <td><Skeleton width={80} /></td>
                  </tr>
                ))
              ) : orders.length > 0 ? (
                orders.map((order) => {
                  const pedidoBadge = getPedidoBadge(order);
                  return (
                    <tr key={order.id} className={styles.tableRow} onClick={() => handleRowClick(order.id)}>
                      <td>#{order.id}</td>
                      <td><strong>{order.cliente_razao_social || 'Cliente não informado'}</strong></td>
                      <td>
                        <span
                          className={styles.statusBadge}
                          style={{ backgroundColor: pedidoBadge.color, color: pedidoBadge.textColor }}
                        >
                          {pedidoBadge.label}
                        </span>
                      </td>
                      <td>
                        {renderEtapasDots(order)}
                      </td>
                      <td>
                        <span className={styles.dateInfo} style={{ fontWeight: 500 }}>
                          {order.operacao_fiscal_desc || order.operacoes_fiscais || '--'}
                        </span>
                      </td>
                      <td>
                        <button className={styles.actionButton} onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(order.id);
                        }}>
                          Ver Detalhes
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className={styles.feedback}>Nenhum pedido encontrado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(pagination?.totalPages ?? 1) > 1 && (
          <div className={styles.paginationRow}>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span>
              Página {pagination?.currentPage ?? 1} de {pagination?.totalPages ?? 1}
            </span>
            <button
              type="button"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, pagination?.totalPages ?? 1))
              }
              disabled={currentPage === (pagination?.totalPages ?? 1)}
            >
              Próxima
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default SampleAnalysisPage;
